import React from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, MessageSquareText, RefreshCw } from 'lucide-react'
import { sdk } from '@/lib/sdk'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { apiErrorText } from '@/lib/sdk-official/resources/member-import'
import { errorParam, errorStatus, retryAfterOf, useCountdown } from './smsShared'

interface PhoneVerificationProps {
  /** The number to confirm, in E.164; without it the API uses the account's phone. */
  phone?: string
  phoneCountry?: string
  /** Sends the first code on mount (the signup step): the person just typed the number. */
  autoSend?: boolean
  onVerified: () => void
  /** The channel is off in this community (409): there is nothing to confirm yet. */
  onUnavailable?: () => void
  className?: string
}

/**
 * F-142: confirm a phone by the 6-digit code. "Enviar código", then the code
 * field with "Reenviar em 0:45" until the API allows another one (one a minute;
 * a 429 restarts the timer with the wait it gives). Used in My Account and in
 * the signup's last step.
 */
export const PhoneVerification = ({ phone, phoneCountry, autoSend, onVerified, onUnavailable, className }: PhoneVerificationProps) => {
  const { t } = useTranslation('sms')
  const [masked, setMasked] = React.useState<string | null>(null)
  /** Where the code went: SMS, or WhatsApp when the community sends it there. */
  const [sentBy, setSentBy] = React.useState<'sms' | 'whatsapp'>('sms')
  const [code, setCode] = React.useState('')
  const [sending, setSending] = React.useState(false)
  const [confirming, setConfirming] = React.useState(false)
  const [error, setError] = React.useState('')
  const { left, start } = useCountdown()
  const sentOnce = React.useRef(false)

  const send = React.useCallback(async () => {
    setSending(true)
    setError('')
    try {
      const result = await sdk.sms.requestPhoneCode(phone ? { phone, ...(phoneCountry ? { phoneCountry } : {}) } : {})
      setMasked(result.phone)
      setSentBy((result.sentVia ?? result.channel) === 'whatsapp' ? 'whatsapp' : 'sms')
      setCode('')
      start(result.resendAfterSeconds || 60)
    } catch (e) {
      const status = errorStatus(e)
      /* 409 on `phone` is sms.phoneAlreadyVerified: the number is already the account's confirmed one. */
      if (status === 409 && errorParam(e) === 'phone') return onVerified()
      /* 409 sms.channelDisabled / channelNotOnPlan: this community sends no code at all. */
      if (status === 409) {
        if (onUnavailable) return onUnavailable()
        return setError(t('verify.unavailable'))
      }
      const wait = retryAfterOf(e)
      if (wait) start(wait)
      /* 503 sms.priceUnknown: the destination's price can't be checked right now; a passing condition, said plainly. */
      setError(status === 503 ? t('verify.tryLater') : apiErrorText(e, t('verify.sendFailed')))
    } finally {
      setSending(false)
    }
  }, [phone, phoneCountry, start, onUnavailable, onVerified, t])

  React.useEffect(() => {
    if (autoSend && !sentOnce.current) {
      sentOnce.current = true
      void send()
    }
  }, [autoSend, send])

  /* Not a <form>: it sits inside the account form, and forms don't nest. Enter confirms here instead of saving the account. */
  const confirm = async () => {
    if (!/^[0-9]{6}$/.test(code)) return setError(t('verify.codeFormat'))
    setConfirming(true)
    setError('')
    try {
      await sdk.sms.confirmPhoneCode(code)
      onVerified()
    } catch (err) {
      setError(apiErrorText(err, t('verify.confirmFailed')))
    } finally {
      setConfirming(false)
    }
  }

  const clock = `${Math.floor(left / 60)}:${String(left % 60).padStart(2, '0')}`

  if (!masked) {
    return (
      <div className={cn('space-y-2', className)} data-testid="phone-verification">
        <Button type="button" variant="outline" size="sm" onClick={() => void send()} disabled={sending || left > 0} data-testid="phone-verification-send">
          {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <MessageSquareText className="mr-2 h-4 w-4" />}
          {left > 0 ? t('verify.resendIn', { time: clock }) : t('verify.send')}
        </Button>
        {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
      </div>
    )
  }

  return (
    <div className={cn('space-y-3', className)} data-testid="phone-verification">
      <p className="text-sm text-gray-600">{t(sentBy === 'whatsapp' ? 'verify.sentToWhatsapp' : 'verify.sentTo', { phone: masked })}</p>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <Input
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          onKeyDown={(e) => {
            if (e.key !== 'Enter') return
            e.preventDefault()
            if (!confirming) void confirm()
          }}
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          placeholder={t('verify.codePlaceholder')}
          aria-label={t('verify.codeLabel')}
          className="h-10 tracking-[0.3em] sm:w-40"
          data-testid="phone-verification-code"
        />
        <Button type="button" onClick={() => void confirm()} variant="secondary" size="sm" className="h-10" disabled={confirming || code.length !== 6} data-testid="phone-verification-confirm">
          {confirming && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {t('verify.confirm')}
        </Button>
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
        {left > 0 ? (
          <span className="text-gray-500" data-testid="phone-verification-timer">{t('verify.resendIn', { time: clock })}</span>
        ) : (
          <button type="button" onClick={() => void send()} disabled={sending} className="inline-flex items-center gap-1.5 font-medium text-primary hover:text-primary-600 disabled:opacity-50">
            <RefreshCw className="h-3.5 w-3.5" />{t('verify.resend')}
          </button>
        )}
        <span className="text-xs text-gray-400">{t('verify.validity')}</span>
      </div>
      {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
    </div>
  )
}
